import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";

const DEFAULT_BRAND = {
  business: {
    name: "Estate Agent",
    short_name: "Agent",
    tagline: "Your property, our priority.",
    hero_headline: "What is your home really worth?",
    hero_sub: "A 60-second online valuation for homeowners and landlords.",
    website_url: "",
    phone: "",
    phone_intl: "",
    email: "",
  },
  // No logo by default. This used to name a real customer's logo file, so
  // any tenant whose brand had not loaded, or had no logo of its own, showed
  // The first customer's mark. Empty means the header and footer set the agency's
  // own name as a wordmark instead.
  assets: { logo_url: "", hero_image_url: "", hero_caption: "" },
  areas_served: [],
  regions_covered_prose: "the local area",
  commuter_belt: "the local market",
  regulatory: { badges: [], footer_lines: [] },
  trust_badges: [],
  testimonial: null,
  colors: null,
  gross_letting_yield: 0.042,
};

const BrandContext = createContext({
  ...DEFAULT_BRAND,
  tenant_slug: null,
  refresh: () => {},
});

/**
 * Nesting depth of the surrounding BrandProviders — the app-level provider
 * is 1, a /t/:slug or admin-scoped provider mounted inside it is 2. The
 * deepest one is the tenant actually on screen, so it owns the palette.
 */
const BrandDepthContext = createContext(0);

/**
 * A tenant's palette is written as inline CSS variables on <html>, where it
 * outranks the `:root` block in index.css until something takes it off again.
 * Providers register a layer here rather than writing the variables straight
 * out, so that whenever the deepest layer changes — a new tenant, or this one
 * unmounting — everything the last palette painted is lifted first. Without
 * that, a tenant whose `colors` are null (most of them) silently inherits the
 * previous tenant's --primary/--accent/--background.
 */
const paletteLayers = [];
let paintedKeys = [];

function repaintPalette() {
  if (typeof document === "undefined") return;
  const top = paletteLayers.reduce(
    (best, layer) => (best && best.depth > layer.depth ? best : layer),
    null,
  );
  const next = (top && top.colors) || {};
  const root = document.documentElement;
  paintedKeys.forEach((k) => {
    if (!(k in next)) root.style.removeProperty(`--${k}`);
  });
  Object.entries(next).forEach(([k, v]) => {
    root.style.setProperty(`--${k}`, v);
  });
  paintedKeys = Object.keys(next);
}

/**
 * BrandProvider — resolves which tenant's brand to display.
 *
 * Priority order:
 *   1. `slug` prop (passed by TenantScope for /t/:slug/* routes)
 *   2. `?tenant=<slug>` query param (used by embed iframes)
 *   3. Current hostname, if it's a tenant's verified custom domain
 *      (e.g. valuation.theiragency.co.uk) — backend falls through to
 *      DEFAULT_TENANT_SLUG if the hostname isn't a recognised domain
 *      (covers foundationagent.co.uk itself, localhost, the bare IP).
 *
 * Colours are applied as CSS variables on <html> so the whole app
 * themes off the active tenant's palette on every switch.
 */
export function BrandProvider({ children, slug: slugProp = null }) {
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [activeSlug, setActiveSlug] = useState(null);
  // Whether the brand call has SETTLED, win or lose — distinct from "the slug
  // is null", which is also true for the moment before it answers. Anything
  // choosing between a tenant's site and Foundation's has to wait for this,
  // or it makes that choice on no information and picks Foundation.
  const [resolved, setResolved] = useState(false);
  const depth = useContext(BrandDepthContext) + 1;

  // Resolve the effective slug (props > URL param)
  const effectiveSlug = useMemo(() => {
    if (slugProp) return slugProp;
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const q = params.get("tenant");
      if (q) return q;
    }
    return null;
  }, [slugProp]);

  const refresh = useCallback(() => {
    let url = "/brand";
    if (effectiveSlug) {
      url = `/brand?tenant=${encodeURIComponent(effectiveSlug)}`;
    } else if (typeof window !== "undefined" && window.location.hostname) {
      url = `/brand?domain=${encodeURIComponent(window.location.hostname)}`;
    }
    return api.get(url)
      .then(({ data }) => {
        if (data) {
          setBrand({ ...DEFAULT_BRAND, ...data });
          setActiveSlug(data.tenant_slug || effectiveSlug || null);
        }
      })
      .catch(() => { /* keep defaults */ })
      .finally(() => setResolved(true));
  }, [effectiveSlug]);

  useEffect(() => { refresh(); }, [refresh]);

  // Apply colours as CSS variables so the whole app themes off the brand,
  // and take them off again on the way out so the next tenant starts from
  // Foundation's own palette rather than this one's.
  useEffect(() => {
    const layer = { depth, colors: brand?.colors || null };
    paletteLayers.push(layer);
    repaintPalette();
    return () => {
      const i = paletteLayers.indexOf(layer);
      if (i > -1) paletteLayers.splice(i, 1);
      repaintPalette();
    };
  }, [brand, depth]);

  const value = useMemo(
    () => ({ ...brand, tenant_slug: activeSlug, brand_resolved: resolved, refresh }),
    [brand, activeSlug, resolved, refresh],
  );

  return (
    <BrandDepthContext.Provider value={depth}>
      <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
    </BrandDepthContext.Provider>
  );
}

/**
 * TenantScope — wraps a route so its child tree uses the /t/:slug tenant's brand.
 *
 * Used by `<Route path="/t/:slug/*" element={<TenantScope>...</TenantScope>}>`.
 * Reads the URL param and hands it to a nested BrandProvider that overrides
 * the app-level default.
 */
export function TenantScope({ children }) {
  const { slug } = useParams();
  return <BrandProvider slug={slug}>{children}</BrandProvider>;
}

export function useBrand() {
  return useContext(BrandContext);
}

/**
 * useTenantSlug — which tenant this page is for, and whether that is yet
 * known well enough to fetch their data.
 *
 * On a tenant's own verified domain there is no slug in the URL, so the slug
 * is only known once `/api/brand?domain=...` comes back. Every public page
 * fell back to "foundation" in the meantime and fetched under it, which asks
 * one agency's site for another agency's data. On the listings page that
 * briefly rendered the wrong properties; on a property page it 404'd and told
 * buyers a live house was "no longer available" before correcting itself.
 *
 * `known` is true immediately when the URL carries the slug (/t/:slug/...),
 * and otherwise once the brand lookup has settled. It settles in a `finally`,
 * so a failed lookup still releases the page rather than hanging it. Callers
 * should hold their fetch until `known`, which costs nothing: the request it
 * replaces was being made against the wrong tenant anyway.
 */
export function useTenantSlug(urlSlug) {
  const brand = useContext(BrandContext);
  return {
    slug: urlSlug || brand.tenant_slug || "foundation",
    known: Boolean(urlSlug) || brand.brand_resolved !== false,
  };
}

/**
 * useMarket — the active tenant's real-estate market ("uk" | "us"),
 * defaulting to "uk" when the brand doesn't declare one. Components pass
 * this to the helpers in lib/terms.js (t / lbl / fmtPrice) so a US tenant
 * renders US wording and currency while every UK tenant is untouched.
 */
export function useMarket() {
  const { market } = useContext(BrandContext);
  return market === "us" ? "us" : "uk";
}

/**
 * useStateProfile — the US state's regulatory profile, or null.
 *
 * US real estate is state-regulated: who runs the closing, whether dual
 * agency is legal, which disclosure form the seller signs and whether there
 * is a transfer tax all change at the state line. The backend resolves all
 * of that from `brand.us_state` (see backend/us_states.py) and ships it
 * inside the brand payload, so a component asks the profile a question
 * rather than branching on the state itself.
 *
 * Null for every UK tenant, and for a US tenant that hasn't picked a state
 * yet — callers must handle that, which `stateAsk()` in lib/usState.js does.
 */
export function useStateProfile() {
  const { state_profile: profile } = useContext(BrandContext);
  return profile || null;
}

/**
 * useTenantPath — builds an internal link that stays on whichever URL
 * space the visitor is currently in.
 *
 * On a tenant's own verified custom domain (e.g. agency.example)
 * routes are bare (`/sell`, `/about`). Under the `/t/:slug/*` preview
 * space (used for previewing other tenants, embeds, or before a domain is
 * attached) the same pages live at `/t/:slug/sell`. Every internal link on
 * an agency page should use this instead of hand-building either form, or
 * clicking around a live custom domain pushes visitors into `/t/:slug/...`
 * URLs that don't belong there.
 */
/**
 * The absolute URL of an agency's OWN public site, for links that leave the
 * CRM and land on it.
 *
 * An agency with a verified custom domain gets that domain and bare paths;
 * everyone else gets the /t/:slug preview space on whatever host the CRM is
 * being served from. The CRM used to hand-build `/t/${slug}` everywhere,
 * which sent the first customer to foundationagent.co.uk/t/<slug> — their own
 * website, on our address.
 *
 * `useTenantPath` is the sibling of this for links WITHIN the public site,
 * which stay relative; this one is for pointing at it from outside.
 */
export function publicSiteUrl(brand, subpath = "") {
  // Nothing until the brand has actually resolved. `tenant_slug` falls back to
  // "foundation" while it is loading, which is truthy — so guarding on the
  // slug alone still produced /t/foundation/properties/<id>, a URL that 404s
  // because Foundation does not own that property, and the public page renders
  // that 404 as "this property is no longer available" about a listing the
  // agent has just put live.
  if (!brand || brand.brand_resolved === false) return "";
  const clean = String(subpath || "").replace(/^\/+/, "");
  const domain = (brand.custom_domain || "").trim();
  if (domain) return `https://${domain}${clean ? `/${clean}` : "/"}`;
  const slug = brand.tenant_slug;
  if (!slug) return "";
  return `/t/${slug}${clean ? `/${clean}` : ""}`;
}


export function useTenantPath() {
  const { slug } = useParams();
  return (subpath) => {
    const clean = String(subpath || "").replace(/^\/+/, "");
    return slug ? `/t/${slug}/${clean}` : `/${clean}`;
  };
}
